import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';

/**
 * Supabase client. PORTED, not redesigned — the old client's auth wiring encodes
 * two fixes that cost real debugging time, and both are carried here:
 *
 * 1. `lock: navigatorLockDisabled` — supabase-js defaults to a Web Locks-based
 *    auth lock which could deadlock the session refresh. The old client bypassed
 *    it (its "Phase 1.2 hotfix"). Dropping the bypass reintroduces a hang that is
 *    intermittent and therefore very expensive to rediscover.
 *
 * 2. The caller must do an EXPLICIT `getSession()` check with a timeout rather
 *    than relying on the `INITIAL_SESSION` event — see `getInitialSession` below.
 *
 * See REBUILD_DECISIONS.md D3: this machinery works. Do not improve it.
 */

/**
 * No-op lock. supabase-js expects a function matching its LockFunc shape; this
 * simply runs the callback without acquiring a Web Lock.
 */
const noopLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => fn();

/**
 * ⚠ A SECOND REFUSAL, and not redundant with the screen in `App`.
 *
 * The stop screen is what a person sees; this is what stops a module that
 * imports the client directly — a background sync, a future entry point, a test
 * harness — from reaching a database on a host whose own environment was never
 * filled in. `createClient` is happy to build a client around a placeholder key;
 * the first failure would then be an opaque 401 somewhere far from the cause.
 */
const url = ENV.misconfigured ? 'https://misconfigured.invalid' : ENV.config.supabaseUrl;
const key = ENV.misconfigured ? 'misconfigured' : ENV.config.supabaseAnonKey;

export const supabase: SupabaseClient = createClient(
  url,
  key,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      lock: noopLock,
    },
  },
);

/** How long to wait for the initial session before rendering the auth screen. */
const INITIAL_SESSION_TIMEOUT_MS = 5_000;

/**
 * Resolve the initial session explicitly.
 *
 * WHY NOT `onAuthStateChange` / `INITIAL_SESSION`: that event is not guaranteed
 * to fire in every load path, so a boot that waits for it can hang on a blank
 * screen forever. The old client learned this and switched to an explicit
 * `getSession()` with a hard timeout. Same here.
 *
 * Returns null on both "no session" and "timed out" — the caller renders the
 * auth screen either way, which is the safe outcome.
 */
export async function getInitialSession() {
  const timeout = new Promise<null>((resolve) =>
    setTimeout(() => resolve(null), INITIAL_SESSION_TIMEOUT_MS),
  );
  const session = supabase.auth
    .getSession()
    .then(({ data }) => data.session ?? null)
    .catch(() => null);
  return Promise.race([session, timeout]);
}
